import { CheckCircleIcon, CheckIcon } from "@heroicons/react/solid";
import { find, findIndex, head } from "lodash";
import React, { Fragment, useMemo } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import classNames from "../utils/classNames";

interface Step {
  key: string;
  title: string;
  component: React.ComponentType<any>;
}

interface Props {
  name: string;
  steps: Step[];
  basePath: string;
  renderExtraSection?: React.ReactNode;
}

const WizardPage: React.FC<Props> = ({
  name,
  steps,
  basePath,
  renderExtraSection
}) => {
  const location = useLocation();
  const history = useHistory();

  const wizardPageMatch = matchPath<{
    stepKey: string;
  }>(location.pathname, {
    path: `${basePath}/:stepKey`
  });

  const currentStep = useMemo(
    () => find(steps, { key: wizardPageMatch?.params.stepKey }),
    [steps, wizardPageMatch?.params.stepKey]
  );

  if (!currentStep) {
    history.push(`${basePath}/${(head(steps) as Step).key}`);
    return null;
  }

  const {
    component: CurrentStepComponent,
    title: currentStepTitle,
    key: currentStepKey
  } = currentStep;

  return (
    <>
      <header className="relative py-10 text-center">
        <div className="flex items-center mb-10 xl:hidden">
          {steps.map((step, i) => {
            const isStepFinished =
              i < findIndex(steps, { key: currentStepKey });
            return (
              <Fragment key={step.key}>
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                  {isStepFinished && (
                    <CheckIcon className="w-3/5" style={{ color: "#A96CEB" }} />
                  )}
                  {!isStepFinished && step.key !== currentStepKey && (
                    <div
                      className="w-6 h-6 rounded-full"
                      style={{ backgroundColor: "#A96CEB" }}
                    />
                  )}
                </div>

                {steps.length !== i + 1 && (
                  <div className="border-t border-white flex-grow" />
                )}
              </Fragment>
            );
          })}
        </div>

        <h1 className="text-2xl md:text-3xl font-bold text-white">
          <span className="hidden md:inline-block">{name}</span>
          <span className="md:hidden">{currentStep.title}</span>
        </h1>
      </header>

      <main>
        <div className="bg-white dark:bg-gray-800 dark:text-white rounded-lg shadow overflow-hidden">
          <div className="divide-y divide-gray-200 dark:divide-gray-500 lg:grid lg:grid-cols-12 lg:divide-y-0 lg:divide-x">
            <aside className="p-6 col-span-3 hidden xl:block">
              {steps.map((step, i) => {
                const stepIndex = findIndex(steps, { key: currentStepKey });
                const isStepFinished = i < stepIndex;
                return (
                  <div className="relative" key={step.key}>
                    <div
                      className={classNames(
                        "p-4 bg-gray-50 dark:bg-gray-700 rounded-md",
                        i !== 0 && "mt-4",
                        step.key !== currentStepKey && "opacity-50"
                      )}
                    >
                      <div className="border-l-4 pl-2 border-hotspotty-400">
                        <div className="font-bold text-hotspotty-400">
                          STEP {i + 1}
                        </div>
                        <div>{step.title}</div>
                      </div>
                    </div>

                    {isStepFinished && (
                      <div className="absolute top-4 right-4 w-5 h-5 text-hotspotty-400">
                        <CheckCircleIcon />
                      </div>
                    )}
                  </div>
                );
              })}
            </aside>

            <div className="p-6 col-span-8 xl:col-span-6">
              <h2 className="text-2xl font-bold hidden md:inline-block mb-2">
                {currentStepTitle}
              </h2>
              <div className="dark:text-gray-300">
                <CurrentStepComponent />
              </div>
            </div>

            {renderExtraSection}
          </div>
        </div>
      </main>
    </>
  );
};

export default WizardPage;
